import { Form, Formik } from "formik";
import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from "react";

import { DeleteOutlineRounded, NorthRounded, StopRounded } from "@mui/icons-material";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { CircularProgress, IconButton } from "@mui/material";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { MarketingSummaryAnalyticsService } from "@analytics/index";

import useFileProcessor from "@hooks/useFileProcessor";

import { UserSummaryFileType } from "@interfaces/chat.type";

import SelectFilePopover from "@pages/Technology/components/SelectFilePopover";

import { useCreateUserFile, useDeleteChatHistory, useLoadMessages } from "@queries/chat.query";

import { PopupActions } from "@store/popupStore";
import { useUserStore } from "@store/userStore";

import theme from "@theme/index";

interface IChatInputProperties {
  canDelete: boolean;
  handleIsDeletingChatHistory: (value: boolean) => void;
  handleSendMessages: (
    value: string,
    file: { fileContent: string; fileName: string } | null,
    handleRemoveFile: () => void,
  ) => Promise<void>;
  isStreaming: boolean;
  stopGenerating: () => void;
  technologyId: string;
}

const handleKeyDown = (event: KeyboardEvent, submitForm: () => void) => {
  if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
    event.preventDefault();
    submitForm();
  }
};

const ChatInput = ({
  canDelete,
  handleIsDeletingChatHistory,
  handleSendMessages,
  isStreaming,
  stopGenerating,
  technologyId,
}: IChatInputProperties) => {
  const [fileContent, setFileContent] = useState<{
    fileContent: string;
    fileName: string;
  } | null>(null);

  const { handleFileProcessing } = useFileProcessor();
  const user = useUserStore();

  const { files: userFiles } = useLoadMessages(technologyId);

  const { mutateAsync: deleteChatHistory } = useDeleteChatHistory();
  const { data, mutateAsync: createUserFile, status: createUserFileStatus } = useCreateUserFile();

  const fileInputReference = useRef<HTMLInputElement>(null);

  const handleFileButtonClick = () => {
    fileInputReference.current?.click();
  };

  const handleFileClick = (file: UserSummaryFileType) => {
    setFileContent({ fileContent: file.structure, fileName: file.fileName });
    void createUserFile({ fileName: file.fileName, structure: file.structure, technologyId, userId: user.id! });
  };

  useEffect(() => {
    if (data) {
      setFileContent({ fileContent: data.structure, fileName: data.fileName });
    }
  }, [data]);

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const fileContent = await handleFileProcessing(file);
      const fileName = file.name;

      if (fileContent && fileContent.length > 0) {
        setFileContent({ fileContent, fileName });
        void createUserFile({ fileName, structure: fileContent, technologyId, userId: user.id! });
      }
    }
  };

  const openChatDeleteModal = () => {
    MarketingSummaryAnalyticsService.trackModalOpened("Delete Marketing Summary");
    PopupActions.openConfirmation({
      content: (
        <Typography sx={{ mt: 1 }}>
          Your chat history, including all responses and shared files, will be removed from this window. This action{" "}
          <b>
            <u>cannot</u>
          </b>{" "}
          be undone.
        </Typography>
      ),
      customOnConfirmText: "Clear",
      isDanger: true,
      onConfirm: () => {
        handleIsDeletingChatHistory(true);
        void deleteChatHistory({ technologyId }).then(() => {
          handleIsDeletingChatHistory(false);
        });
      },
      title: "Clear Conversation",
    });
  };

  const handleRemoveFile = () => {
    setFileContent(null);
    if (fileInputReference.current) {
      fileInputReference.current.value = "";
    }
    MarketingSummaryAnalyticsService.trackFileRemoved();
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderLeft: `1px solid ${theme.palette.grey[300]}`,
        borderRight: `1px solid ${theme.palette.grey[300]}`,
        borderTop: `1px solid ${theme.palette.grey[300]}`,
        borderTopLeftRadius: "8px",
        borderTopRightRadius: "8px",
        maxWidth: 760,
        mx: "auto",
        width: "100%",
      }}
    >
      <Formik
        initialValues={{ input: "" }}
        onSubmit={(values, formikHelpers) => {
          if (isStreaming) {
            stopGenerating();
          } else {
            void formikHelpers.setFieldValue("input", "");
            void handleSendMessages(values.input, fileContent, handleRemoveFile);
          }
        }}
      >
        {({ setFieldValue, submitForm, values }) => {
          return (
            <Form>
              <Box>
                <TextField
                  minRows={2}
                  multiline
                  name="input"
                  onChange={(event) => void setFieldValue("input", event.target.value)}
                  onKeyDown={(event) => handleKeyDown(event, submitForm)}
                  placeholder="Tell me about your innovation..."
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "& .MuiOutlinedInput-root": {
                      border: "none",
                    },
                    backgroundColor: theme.palette.background.paper,
                    border: "none",
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                    borderTopLeftRadius: "8px",
                    borderTopRightRadius: "8px",
                    margin: 0,
                    maxHeight: 180,
                    overflow: "auto",
                    width: "100%",
                  }}
                  value={values.input}
                />
                <Box
                  sx={{
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "space-between",
                    pb: 2,
                    px: 2,
                  }}
                >
                  <Box>
                    {fileContent ? (
                      <Box
                        sx={{
                          alignItems: "center",
                          backgroundColor: theme.palette.common.white,
                          border: `1px solid ${theme.palette.grey[300]}`,
                          borderRadius: "8px",
                          display: "flex",
                          gap: 1,
                          height: "40px",
                          pl: 2,
                          pr: 3,
                        }}
                      >
                        <IconButton
                          onClick={handleRemoveFile}
                          sx={{
                            "&:hover": {
                              color: theme.palette.error.main,
                            },
                            height: "30px",
                            transition: "color 0.1s ease-in-out",
                            width: "30px",
                          }}
                        >
                          <CloseRoundedIcon />
                        </IconButton>

                        <Typography sx={{ alignItems: "center", display: "flex", gap: 1 }} variant="body2">
                          {fileContent.fileName}{" "}
                          {createUserFileStatus === "pending" ? (
                            <CircularProgress size={18} />
                          ) : (
                            <CheckCircleOutlineRoundedIcon
                              sx={{
                                color: theme.palette.primary.main,
                                fontSize: "20px",
                              }}
                            />
                          )}
                        </Typography>
                      </Box>
                    ) : (
                      <SelectFilePopover
                        files={userFiles}
                        handleFileButtonClick={handleFileButtonClick}
                        handleFileClick={handleFileClick}
                      />
                    )}
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton
                      disabled={!canDelete}
                      onClick={openChatDeleteModal}
                      sx={{
                        "&:hover": {
                          color: theme.palette.error.main,
                        },
                        height: "30px",
                        ml: 0.5,
                        transition: "color 0.1s ease-in-out",
                        width: "30px",
                      }}
                    >
                      <DeleteOutlineRounded sx={{ fontSize: "20px" }} />
                    </IconButton>
                    <IconButton
                      disabled={(values.input.trim() === "" && !isStreaming) || createUserFileStatus === "pending"}
                      sx={{
                        "&:hover": {
                          backgroundColor: theme.palette.primary.dark,
                        },
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.common.white,
                        height: "30px",
                        width: "30px",
                      }}
                      type="submit"
                    >
                      {isStreaming ? (
                        <StopRounded sx={{ fontSize: "20px" }} />
                      ) : (
                        <NorthRounded sx={{ fontSize: "20px" }} />
                      )}
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </Form>
          );
        }}
      </Formik>
      <input
        accept=".pdf,.docx,.txt"
        onChange={handleFileChange}
        ref={fileInputReference}
        style={{ display: "none" }}
        type="file"
      />
    </Box>
  );
};

export default ChatInput;
